import { FormInstance, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import produce from 'immer';
import { create } from 'zustand';
import { ISiteAccount } from '~/@core/domain/model/site/SiteAccount';
import AutoCategory from '~/domain/model/ServiceRequests/AutoCategory';
import Category from '~/domain/model/ServiceRequests/Category';
import SubCategory from '~/domain/model/ServiceRequests/SubCategory';
import {
  checkIfDeskManagerUserIsValid,
  createServiceRequest,
  getServiceRequestsAutoCategories,
  getServiceRequestsCategories,
  getServiceRequestsSubCategories,
  IAttachments,
  ICreateServiceRequestParams,
  IFetchParams,
  IServiceRequestResponse
} from '~/services/producer/ServiceRequests/ServiceRequestsService';
import { getSitesByLoggedUser } from '~/services/producer/site/SiteService';

export type IActions = {
  setData: (fn: (state: IServiceRequests) => void) => void;
  setFormInstance: (form: Partial<FormInstance<IFormFilter>>) => void;
  getSites: () => void;
  getFilters: () => Promise<void>;
  getCategories: () => void;
  getSubCategories: () => void;
  getAutoCategories: () => void;
  isEmailValid: (email: string) => Promise<boolean>;
  validateEmailAndLoadFilters: (email: string) => void;
  sendAttachmentFile: (file: UploadChangeParam<UploadFile>) => void;
  createServiceRequest: () => Promise<IServiceRequestResponse>;
  resetFilters: () => void;
  clearAttachmentFile: () => void;
};

export type ISelectors = {
  isFiltersSuccessful: () => boolean;
};

export type IGetters = {
  getAttachments: () => IAttachments[];
}

export type IFormFilter = {
  siteId: number | null;
  categoryId: number | null;
  subCategoryId: number | null;
  autoCategoryId: number | null;
  description: string | null;
  menuOrPage: string | null;
  gfpQuantity: number | null;
  requesterName: string | null;
};

export type IFilters = {
  sites: {
    data: ISiteAccount[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  categories: {
    data: Category[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  subCategories: {
    data: SubCategory[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  autoCategories: {
    data: AutoCategory[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
    isFailure: boolean;
    isReports: boolean;
    isBillingOutsideSP: boolean;
    isBillingInsideSP: boolean;
  };
  attachments: {
    data: UploadFile[];
    info: IAttachments[];
    isLoading: boolean;
  };
  email: {
    data: string;
    isValid: boolean;
  };

  formFilter: IFormFilter;
};

export type IServiceRequests = {
  filters: IFilters;
  formFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  buttonDisabled: boolean;
} & IActions &
  ISelectors & IGetters;

export const initialStateFilters: IFilters = {
  sites: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: ''
  },
  categories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: ''
  },
  subCategories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: ''
  },
  autoCategories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
    isFailure: false,
    isReports: false,
    isBillingOutsideSP: false,
    isBillingInsideSP: false
  },
  attachments: {
    data: [],
    info: [],
    isLoading: false
  },
  email: {
    data: '',
    isValid: false
  },

  formFilter: {
    siteId: null,
    categoryId: null,
    subCategoryId: null,
    autoCategoryId: null,
    description: null,
    menuOrPage: null,
    gfpQuantity: null,
    requesterName: null
  }
};

const useServiceRequestsStore = create<IServiceRequests>()((set, get) => ({
  // States
  filters: initialStateFilters,
  formFilterInstance: null,
  buttonDisabled: false,

  // Setters
  setData: fn => set(produce(fn)),
  setFormInstance: (form: Partial<FormInstance<IFormFilter>>) => {
    set({ formFilterInstance: form });
  },

  // Selectors
  isFiltersSuccessful: () => {
    const { sites, categories, subCategories, autoCategories } = get().filters;
    return (
      sites.isSuccessful &&
      categories.isSuccessful &&
      subCategories.isSuccessful &&
      autoCategories.isSuccessful
    );
  },


  // Getters
  getAttachments: () => {
    return get().filters.attachments.info || [];
  },

  // Actions
  getSites: async () => {
    const { setData } = get();

    try {
      setData(state => {
        state.filters.sites.isLoading = true;
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = false;
      });

      const { data } = await getSitesByLoggedUser();

      setData(state => {
        state.filters.sites.data = data;
        state.filters.sites.isSuccessful = true;
        state.filters.sites.isError = false;
      });
      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.sites.data = [];
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = true;
      });
      return Promise.reject(error);
    } finally {
      setData(state => {
        state.filters.sites.isLoading = false;
      });
    }
  },
  getCategories: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.categories.isLoading = true;
      state.filters.categories.isSuccessful = false;
      state.filters.categories.isError = false;
    });

    try {
      const params: IFetchParams = {
        Pesquisa: '',
        Ativo: 'S',
        isProducer: true
      };

      const { data } = await getServiceRequestsCategories(params);

      setData(state => {
        state.filters.categories.data = data;
        state.filters.categories.isSuccessful = true;
        state.filters.categories.isError = false;
      });
    } catch (error) {
      setData(state => {
        state.filters.categories.data = [];
        state.filters.categories.isSuccessful = false;
        state.filters.categories.isError = true;
      });
    } finally {
      setData(state => {
        state.filters.categories.isLoading = false;
      });
    }
  },
  getSubCategories: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.subCategories.isLoading = true;
      state.filters.subCategories.isSuccessful = false;
      state.filters.subCategories.isError = false;
    });

    try {
      const categoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('categoryId') ?? null;

      const categoryNameSelected =
        get().filters.categories.data.find(
          i => i.account === categoryIdSelected
        )?.name || '';

      const params: IFetchParams = {
        Pesquisa: categoryNameSelected,
        Ativo: 'S',
        isProducer: true
      };

      const { data } = await getServiceRequestsSubCategories(params);

      setData(state => {
        state.filters.subCategories.data = data;
        state.filters.subCategories.isSuccessful = true;
        state.filters.subCategories.isError = false;
      });
    } catch (error) {
      setData(state => {
        state.filters.subCategories.data = [];
        state.filters.subCategories.isSuccessful = false;
        state.filters.subCategories.isError = true;
      });
    } finally {
      setData(state => {
        state.filters.subCategories.isLoading = false;
      });
    }
  },
  getAutoCategories: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.autoCategories.isLoading = true;
      state.filters.autoCategories.isSuccessful = false;
      state.filters.autoCategories.isError = false;
    });

    try {
      const categoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('categoryId') ?? null;
      const subCategoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('subCategoryId') ?? null;

      const categoryNameSelected =
        get().filters.categories.data.find(
          category => category.account === categoryIdSelected
        )?.name || '';
      const subCategoryNameSelected =
        get().filters.subCategories.data.find(
          subCategory => subCategory.account === subCategoryIdSelected
        )?.name || '';

      const params: IFetchParams = {
        Pesquisa:
          categoryNameSelected && subCategoryNameSelected
            ? `${categoryNameSelected} - ${subCategoryNameSelected}`
            : categoryNameSelected || subCategoryNameSelected || '',
        Ativo: 'S',
        isProducer: true
      };

      const { data } = await getServiceRequestsAutoCategories(params);

      setData(state => {
        state.filters.autoCategories.data = data;
        state.filters.autoCategories.isSuccessful = true;
        state.filters.autoCategories.isError = false;
      });
    } catch (error) {
      setData(state => {
        state.filters.autoCategories.data = [];
        state.filters.autoCategories.isSuccessful = false;
        state.filters.autoCategories.isError = true;
      });
    } finally {
      setData(state => {
        state.filters.autoCategories.isLoading = false;
      });
    }
  },
  getFilters: async () => {
    const { getSites, getCategories, getSubCategories, getAutoCategories } =
      get();
    try {
      await Promise.all([
        getSites(),
        getCategories(),
        getSubCategories(),
        getAutoCategories()
      ]);
      return Promise.resolve();
    } catch (error) {
      console.error('Error ao carregar filtros.', error);
      return Promise.reject(error);
    }
  },
  resetFilters: () => {
    const { setData } = get();
    setData(state => {
      state.filters = initialStateFilters;
    });
  },
  isEmailValid: async (email: string) => {
    try {
      const response = await checkIfDeskManagerUserIsValid(email);
      const isUserValid = response.data;

      if (!isUserValid) {
        return Promise.reject();
      }
      return Promise.resolve(true);
    } catch (error) {
      console.error('Erro ao validar usuário.', error);
      return Promise.reject();
    }
  },
  validateEmailAndLoadFilters: async (email: string) => {
    const { setData, isEmailValid, getFilters } = get();
    const userExists = await isEmailValid(email);

    if (userExists) {
      getFilters();
      setData(state => {
        state.filters.email.data = email;
        state.filters.email.isValid = true;
      });
    } else {
      throw new Error(
        `O e-mail ${email} não foi cadastrado na central de suporte.`
      );
    }
  },
  sendAttachmentFile: async (file: UploadChangeParam<UploadFile>) => {
    const { setData } = get();

    setData(state => {
      state.filters.attachments.data = file.fileList.map(f => ({
        ...f,
        status: 'uploading'
      }));
    });

    try {
      const attachments: IAttachments[] = await Promise.all(
        file.fileList.map(fileItem => {
          const originFile = fileItem.originFileObj;
          return new Promise<IAttachments>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(originFile as Blob);
            reader.onload = () => {
              const base64String = (reader.result as string).split(',')[1];
              resolve({
                base64: base64String,
                name: fileItem.name,
                type: fileItem.type || 'application/octet-stream'
              });
            };
            reader.onerror = error => reject(error);
          });
        })
      );

      setData(state => {
        state.filters.attachments.info = attachments;
      });

      setData(state => {
        state.filters.attachments.data = file.fileList.map(f => ({
          ...f,
          status: 'done'
        }));
      });
    } catch (error) {
      console.error('Erro ao processar anexos', error);

      setData(state => {
        state.filters.attachments.data = file.fileList.map(f => ({
          ...f,
          status: 'error'
        }));
      });
    }
  },
  clearAttachmentFile: () => {
    const { setData } = get();
    setData(state => {
      state.filters.attachments.data = [];
      state.filters.attachments.info = initialStateFilters.attachments.info;
    });
  },
  createServiceRequest: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.attachments.isLoading = true;
      state.buttonDisabled = true;
    });

    const email = get().filters.email.data;
    const formValues = get().formFilterInstance?.getFieldsValue?.();
    const periodDate =
      get().formFilterInstance?.getFieldValue?.('periodDate') ?? [];

    const autoCategoryAccountSelected =
      get().formFilterInstance?.getFieldValue?.('autoCategoryId') ?? null;

    const selectedCategoryName = get().filters.autoCategories.data.find(
      autoCategory => autoCategory.account === autoCategoryAccountSelected
    )?.name;

    const description =
      get().formFilterInstance?.getFieldValue?.('description');

    const siteIdSelected =
      get().formFilterInstance?.getFieldValue?.('siteId') ?? null;
    const siteAccountCodeSelected = get().filters.sites.data.find(
      site => site.code === siteIdSelected
    )?.code;

    const menuOrPage = get().formFilterInstance?.getFieldValue?.('menuOrPage');
    const completeName =
      get().formFilterInstance?.getFieldValue?.('completeName');
    const initialPeriod =
      periodDate.length > 0 ? periodDate[0].format('DD-MM-YYYY') : '';
    const finalPeriod =
      periodDate.length > 0 ? periodDate[1].format('DD-MM-YYYY') : '';

    const isFailure = get().filters.autoCategories.isFailure;
    const isReports = get().filters.autoCategories.isReports;
    const isBilling = get().filters.autoCategories.isBillingOutsideSP || get().filters.autoCategories.isBillingInsideSP;
    const attachments = get().getAttachments();

    try {
      const params: ICreateServiceRequestParams = {
        TChamado: {
          Email: email,
          Solicitacao: '0001',
          Assunto: selectedCategoryName as string,
          AutoCategoria: autoCategoryAccountSelected,
          Descricao: description,
          TicketNumber: '',
          Attachment: attachments
        },
        TCampoExtra: {
          accountCode: siteAccountCodeSelected ?? '',
          menuOrPageFailure: isFailure ? menuOrPage : '',
          CompletName: isReports ? completeName : '',
          InicialPeriod: isReports ? initialPeriod : '',
          FinalPeriod: isReports ? finalPeriod : '',
          gfpQuantity: isBilling && formValues?.gfpQuantity ? formValues.gfpQuantity : Number.NaN,
          requesterName: isBilling && formValues?.requesterName ? formValues.requesterName : ''
        }
      };

      const { data } = await createServiceRequest(params);
      setData(state => {
        state.filters.attachments.isLoading = false;
        state.buttonDisabled = false;
      });
      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.attachments.isLoading = false;
        state.buttonDisabled = false;
      });
      return Promise.reject(error);
    }
  }
}));

export default useServiceRequestsStore;
